import type { FC, ReactNode } from 'react';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import memoizeOne from 'memoize-one';
import isEqual from 'lodash/isEqual';
import { v4 as uuid } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { Config as CollabProviderConfig } from '@atlaskit/collab-provider/types';

import {
	EDIT_PAGE_LOAD_EXPERIENCE,
	COMPANY_HUB_EDITOR_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStop } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';
import { getCollabEditProvider } from '@confluence/fabric-providers/entry-points/NativeCollabProvider.ts';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { usePreloadedDraft } from '@confluence/fabric-providers/entry-points/usePreloadedDraft.ts';
import { useFetchUsersClient } from '@confluence/fabric-providers/entry-points/useFetchUsersClient.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import {
	useEditorFlagsContext,
	withEditorFlagsContext,
} from '@confluence/editor-flags/entry-points/EditorFlagsContext.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import {
	queryInterop,
	type QueryInteropSupportedResult,
} from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { useShowConvertToLivePageFlag } from '@confluence/editor-flags/entry-points/useShowConvertToLivePageFlag.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useExperimentalResetCollabServiceMutation } from '@confluence/fabric-providers/entry-points/useExperimentalResetCollabServiceMutation.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { EDIT_PAGE_V2, EDIT_BLOG_V2 } from '@confluence/named-routes/entry-points/routes.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';

import { useEditorHeartbeat } from './useEditorHeartbeat/useEditorHeartbeat.ts';
import { useInit } from './useInit.ts';
import { usePresence } from './usePresence.ts';
import { useMetadataChanged } from './useMetadataChanged.ts';
import type { NativeCollabInjectedProps } from './commonCollabTypes.ts';
import { NativeCollabQuery } from './NativeCollabQuery.graphql.ts';
import type { NativeCollabQuery as NativeCollabQueryResponse } from './__types__/NativeCollabQuery.ts';
import { useError } from './useError.ts';
import { useDisconnected } from './useDisconnected.ts';
import { useCommitStatus } from './useCommitStatus.ts';
import { useConnected } from './useConnected.ts';
import {
	DraftSyncAndPublishContextConsumer,
	DraftSyncAndPublishContextProvider,
	useDraftSyncAndPublishContext,
} from './DraftSyncAndPublishContext.tsx';
import { useNativeCollabState } from './useNativeCollabState.ts';
import { useCollabInitTimeout } from './useCollabInitTimeout.ts';
import { useCollabConnectedTimeout } from './useCollabConnectedTimeout.ts';
import { nativeCollabPermissionsTokenState } from './CollabPermissionsTokenState.ts';
import { useNewDraftEventTrigger } from './useNewDraftEventTrigger.ts';
import { useCollabPermissionChange } from './useCollabPermissionChange.ts';
import { useCollaboratorLimits } from './useCollaboratorLimits.tsx';
import { useNativeCollabPresenceState } from './useNativeCollabPresenceState.ts';
import { useCollabNamespaceLockCheck } from './useCollabNamespaceLockCheck.ts';
import { useNativeCollabConnectionListener } from './useNativeCollabConnectionListener.tsx';

type NativeCollabProps = {
	contentId: string;
	spaceKey: string;
	draftShareId?: string;
	contentType: string;
	disableInviteToEdit?: boolean;
	permissionTokenRefresh?: CollabProviderConfig['permissionTokenRefresh'];
	children: (props: NativeCollabInjectedProps, ...rest: any) => ReactNode;
	pageTreeStateUpdater?: PageTreeStateUpdater;
	onPermissionChange?: () => void;
	flags: FlagsStateContainer;
	canEdit?: boolean;
};

type DraftMetadata = {
	isUnpublishedDraft: boolean;
	version: number;
	spaceId: string | null;
	subType: string | null;
	isOverviewPage: boolean;
};
const getDraftMetadata = (
	data: NativeCollabQueryResponse | undefined,
	loading: boolean,
	draftShareId: string | undefined,
): DraftMetadata => {
	const content = data?.content?.nodes?.[0];
	return {
		// We use the presence of a draftShareId as a best guess for the isUnpublishedDraft value while this query is loading.
		isUnpublishedDraft: loading ? !!draftShareId : !content?.history?.createdBy,
		version: content?.version?.number || 1,
		spaceId: content?.space?.id || null,
		subType: content?.subType || null,
		isOverviewPage: !!content && content.space?.homepageId === content.id,
	};
};

const childrenMemoized = memoizeOne(
	(children: NativeCollabProps['children'], props: NativeCollabInjectedProps) => children(props),
	isEqual,
);

const NativeCollabComponentClass: FC<NativeCollabProps> = (props) => {
	const {
		children,
		contentId,
		contentType,
		draftShareId,
		spaceKey,
		permissionTokenRefresh,
		pageTreeStateUpdater,
		onPermissionChange,
		flags,
		canEdit,
	} = props;
	const { replace } = useRouteActions();
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const { cloudId, userId, activationId, isRovoLLMEnabled } = useSessionData();

	const isLivePage = useIsCurrentPageLive();
	const { getIsPublishing } = useDraftSyncAndPublishContext();
	const [{ collabEditProvider }, { setCollabEditProvider, resetNativeCollabState }] =
		useNativeCollabState();
	const { forceRefetchBlogTree } = useBlogTreeActions();

	const [forceDisabled, setForceDisabled] = useState<boolean>(false);
	const lastPublishedVersionRef = useRef<number | null>(null);

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const { client: fetchUsersClient } = useFetchUsersClient();

	const [nativeCollabQueryResult, setNativeCollabQueryResult] = useState<
		(QueryInteropSupportedResult<NativeCollabQueryResponse> & { loading: boolean }) | undefined
	>(undefined);
	const { loading, error, data } = {
		loading: nativeCollabQueryResult?.loading ?? true,
		error: nativeCollabQueryResult?.error,
		data: nativeCollabQueryResult?.data,
	};
	const fetchNativeCollab = useCallback(
		async (shouldCheckCache: boolean) => {
			// Fetch the editor draft metadata
			const queryResult = await queryInterop<NativeCollabQueryResponse>({
				query: NativeCollabQuery,
				variables: {
					contentId,
					status: ['draft', 'current'],
					draftShareId,
				},
				fetchPolicy: shouldCheckCache ? 'cache-first' : 'network-only',
			});

			// Sometimes, Apollo fetches data from the cache but returns an empty result.
			// In these situations, the code below will refetch the latest data from the server.
			//
			// This can happen when archiving a page, unarchiving it, and entering the editor in the same SPA session.
			// The NativeCollabQuery result is still in the GraphQL cache, so Apollo attempts to fetch it from the cache.
			// But for some reason it fails, and it returns an empty result.
			// This empty result can cause bugs in the editor, like an incorrect isUnpublishedDraft value.
			// I believe this may be a bug in Apollo.
			if (
				shouldCheckCache &&
				Array.isArray(queryResult?.data?.content?.nodes) &&
				queryResult?.data?.content?.nodes.length === 0
			) {
				return await fetchNativeCollab(false);
			}

			setNativeCollabQueryResult({ ...queryResult, loading: false });

			return queryResult?.data;
		},
		[contentId, draftShareId],
	);

	useEffect(() => {
		void fetchNativeCollab(true);
	}, [fetchNativeCollab]);

	useEffect(() => {
		if (isLivePage || nativeCollabQueryResult?.loading || nativeCollabQueryResult?.error) {
			return;
		}

		const contentNode = nativeCollabQueryResult?.data?.content?.nodes?.[0];
		const fetchedContentType = contentNode?.type;
		// only handle this for pages or blogposts to be safe
		const isValidType = fetchedContentType === 'page' || fetchedContentType === 'blogpost';

		if (!isValidType || fetchedContentType === contentType) {
			return;
		}

		const redirectRoute = fetchedContentType === 'page' ? EDIT_PAGE_V2 : EDIT_BLOG_V2;

		replace(
			redirectRoute.toUrl({
				spaceKey,
				contentId,
				contentType: fetchedContentType === 'page' ? 'pages' : 'blog',
			}),
		);
	}, [nativeCollabQueryResult, contentType, spaceKey, contentId, replace, isLivePage]);

	const [resetCollabService] = useExperimentalResetCollabServiceMutation({
		variables: {
			behind: 'COLLAB',
			contentId,
		},
	});

	const memoizedDraftMetadata: DraftMetadata = useMemo(
		() => getDraftMetadata(data, loading, draftShareId),
		[data, loading, draftShareId],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// clean-up for Native collab provider state when component unmounts
	useEffect(() => {
		const sendEditTrackEvent = (action: string, exitedConfluence?: boolean) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action,
					actionSubject: 'nativeEditor', // "editor mounted" is taken
					source: 'nativeCollabComponent',
					attributes: {
						exitedConfluence,
						isLivePage,
					},
				},
			}).fire();
		};

		const onBeforeUnload = () => {
			sendEditTrackEvent('closed', true);
		};

		sendEditTrackEvent('opened');
		window.addEventListener('beforeunload', onBeforeUnload);

		return () => {
			sendEditTrackEvent('closed');
			window.removeEventListener('beforeunload', onBeforeUnload);

			const transitionPreloaderCurrentCallbackIDs =
				getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
			const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
				? transitionPreloaderCurrentCallbackIDs.includes(
						'live_doc_preloading_transition_callback_id',
					)
				: false;

			if (!isSeamlessTransition) {
				resetNativeCollabState();
				nativeCollabPermissionsTokenState.clearPermissionsTokenState(contentId);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { preloadedDraftPromise } = usePreloadedDraft(contentId);

	const [{ isViewMode, mode }, { setMode }] = useLivePageMode();

	// At client-side rendering, while waiting for mode to have data, initialize it with canEdit
	// if it's available from SSRed result
	const initialViewMode = mode === undefined && canEdit !== undefined ? !canEdit : isViewMode;

	useEffect(() => {
		// Live Docs rely on useIsEditorDataLoader to set collab edit provider
		if (isLivePage) return;

		const activity = initialViewMode ? 'viewer' : 'editor';

		void preloadedDraftPromise.then((preloadedDraft) => {
			// getCollabEditProvider call getAnalyticsWebClient and return a Promise with collabEditProvider
			// and analyticsClient passed in as argument

			// DO NOT add any asynchronous calls/behavior to the collab provider creation. This has caused incidents
			// in the past where the provider promise never resolves and editing breaks.
			// Context: https://hello.atlassian.net/wiki/spaces/~589311246/pages/2554442259/Investigate+async+issue+HOT-103180
			const provider = getCollabEditProvider({
				contentId,
				cloudId,
				activationId,
				contentType,
				permissionTokenRefresh,
				initialDraft: preloadedDraft,
				isLivePage,
				presenceId,
				presenceActivity: activity,
				userId,
				fetchUsersClient,
			});

			setCollabEditProvider(provider);

			// marking provider creation
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_CREATED,
			);
			// at this point, provider has been created but is not yet initialized
			CollabEditProviderMeasures.markMeasureStart(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_DISABLED,
			);
			// marking post-creation -> pre-connection time
			CollabEditProviderMeasures.markMeasureStart(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_PRE_CONNECTED,
			);
		});
	}, [
		activationId,
		cloudId,
		contentId,
		contentType,
		initialViewMode,
		isLivePage,
		presenceId,
		userId,
		permissionTokenRefresh,
		preloadedDraftPromise,
		setCollabEditProvider,
		fetchUsersClient,
	]);

	// Clean-up
	useEffect(() => {
		return () => {
			// Explicitly call destroy so the disconnect happens
			const transitionPreloaderCurrentCallbackIDs =
				getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
			const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
				? transitionPreloaderCurrentCallbackIDs.includes(
						'live_doc_preloading_transition_callback_id',
					)
				: false;

			if (collabEditProvider && !isSeamlessTransition) {
				collabEditProvider?.destroy();
				// Clear the memoized function. If the user goes back to editing the same page,
				// then we don't run into problem trying to talk to the old (destroyed) collab provider.
				if (getCollabEditProvider.cache?.clear) {
					getCollabEditProvider.cache.clear();
				}
			}
		};
	}, [collabEditProvider]);

	// Collab native events
	const { collabEditInitialized, titleFromInit, isDocEmptyRef, onConnected } = useInit(
		collabEditProvider,
		contentId,
		isCompanyHub,
		isRovoLLMEnabled,
	);

	const handleMetadataChange = useCallback(() => {
		if (contentType === 'blogpost') {
			// update blog tree to show untitled blank blog draft after it is created
			forceRefetchBlogTree();
		}

		// If we are on a live page, refresh the page tree so the new title is reflected in the page tree
		if (isLivePage) pageTreeStateUpdater?.setForceFetchPageId(contentId);
	}, [isLivePage, pageTreeStateUpdater, contentId, contentType, forceRefetchBlogTree]);
	// Trigger `handleMetadataChange` wheneven a title changed event is received from NCS.
	const { onTitleChange, title } = useMetadataChanged(
		collabEditProvider,
		handleMetadataChange,
		isLivePage,
		isRovoLLMEnabled,
		isDocEmptyRef,
	);
	// NOTE: The logic to refresh the PageTree on title emoji change is in EmojiTitleButton.ts

	// Trigger `handleNamespaceLockCheck` when `namespace-lock:check` event is emitted from NCS.
	const { isLocked } = useCollabNamespaceLockCheck(collabEditProvider);

	const { isFirstParticipant, isSingleUserSession } = usePresence(collabEditProvider);
	const { showEditorFlag } = useEditorFlagsContext();
	const [isUnpublishedDraft, setIsUnpublishedDraft] = useState<boolean>(
		memoizedDraftMetadata.isUnpublishedDraft,
	);
	const [version, setVersion] = useState<number>(memoizedDraftMetadata.version);
	const [subType, setSubtype] = useState<string | null>(null);
	const [isOverviewPage, setIsOverviewPage] = useState<boolean>(false);

	useEffect(() => {
		setIsUnpublishedDraft(memoizedDraftMetadata.isUnpublishedDraft);
		setVersion(memoizedDraftMetadata.version);
		setSubtype(memoizedDraftMetadata.subType);
		setIsOverviewPage(memoizedDraftMetadata.isOverviewPage);
	}, [
		memoizedDraftMetadata.version,
		memoizedDraftMetadata.isUnpublishedDraft,
		memoizedDraftMetadata.subType,
		memoizedDraftMetadata.isOverviewPage,
	]);

	// Editor flag for when another user publishes the page
	const showPublishedFlagOnce = useCallback(
		(newVersion, lastPublisherId) => {
			if (lastPublisherId !== userId || (isLivePage && !getIsPublishing())) {
				if (lastPublishedVersionRef.current === newVersion) {
					// We don't want to show the flag if it's already been shown for this version
					return;
				}

				// If the last publisher is not the current user, show the flag
				// On a live page, we'll always show the flag no matter who the last publisher is unless you are the one publishing on this tab.
				// The intent of the live pages flag showing for yourself is in case you have multiple tabs open and you publish on one of them.
				lastPublishedVersionRef.current = newVersion;
				showEditorFlag({
					errorName: ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH,
					statusCode: newVersion,
					lastPublisher: lastPublisherId,
				});
			}
		},
		[getIsPublishing, isLivePage, showEditorFlag, userId],
	);

	const onPublishedSubscriptionEvent = useCallback(
		(newVersion: any, accountId: any) => {
			if (!newVersion) return;

			setVersion(newVersion);
			setIsUnpublishedDraft(false);

			if (isLivePage) {
				setForceDisabled(true);
			}

			const lastPublisherId = accountId;
			showPublishedFlagOnce(newVersion, lastPublisherId);
		},
		[isLivePage, showPublishedFlagOnce],
	);

	// Editor flag for when another user restores the page to a previous version
	const onRestoreSubscriptionEvent = useCallback(
		(accountId: any) => {
			if (accountId !== userId) {
				showEditorFlag({
					errorName: ALLOWED_FLAG_MESSAGES.OTHER_RESTORE,
					lastPublisher: accountId,
				});
			}
		},
		[showEditorFlag, userId],
	);

	const onPageUpdatedSubscription = useCallback(
		(response: any) => {
			const { pageUpdated, accountId } = response || {};

			const trigger = pageUpdated?.trigger;
			const confVersion = pageUpdated?.confVersion;

			if (trigger === 'convertToLiveEdit' && contentType === 'page') {
				setForceDisabled(true);
			} else if (trigger === 'restore') {
				onRestoreSubscriptionEvent(accountId);
			} else if (trigger === 'publish') {
				onPublishedSubscriptionEvent(confVersion, accountId);
			}
		},
		[contentType, onPublishedSubscriptionEvent, onRestoreSubscriptionEvent],
	);

	useSubscriptionEvent({
		subscriberId: 'NativeCollabComponent',
		contentId,
		delta: 'pageUpdated',
		contentType,
		onDataReceived: onPageUpdatedSubscription,
	});

	useNewDraftEventTrigger(contentId, data, isUnpublishedDraft);

	// Collab unhappy path events
	const { error: collabEditError, recoveryError: collabRecoveryError } = useError(
		// Note: errors will be emitted after editor load, once we connect to collab service
		collabEditProvider,
		resetCollabService,
		contentId,
		contentType,
	);
	const { internetLost: collabEditInternetLost, disconnected } = useDisconnected(
		collabEditProvider,
		contentId,
		contentType,
	);

	const { hasExceededMaxStepFailures } = useCommitStatus(
		collabEditProvider,
		contentId,
		contentType,
	);

	const { isCollabProviderConnected } = useConnected(collabEditProvider, contentId, onConnected);
	const { collabEditConnectionTimedOut } = useCollabConnectedTimeout(
		collabEditInitialized,
		isCollabProviderConnected,
		onConnected,
	);

	const heartbeatData = useEditorHeartbeat({
		contentId,
		contentType,
		collabEditInitialized,
		viewMode: initialViewMode,
	});

	useEffect(() => {
		if (isLivePage && heartbeatData.liveEditLimitReached) {
			setMode('view');
		}
	}, [isLivePage, heartbeatData.liveEditLimitReached, setMode]);

	const showConvertToLivePageFlag = useShowConvertToLivePageFlag({ flags });
	useEffect(() => {
		if (!heartbeatData.subtypeMismatch || !fg('confluence_ncs_disconnect_idle_tabs_pubsub')) return;

		// If we detect subtypeMismatch===true, it means that the backend state is out of sync with the frontend.
		// This usually happens when this tab was idle. Since pubsub events are disabled on idle tabs,
		// this tab missed that change event and only discovered it through the heartbeat response

		const missedPageConvertedToLive = !isLivePage && heartbeatData.heartbeatSubtype === 'live';
		if (missedPageConvertedToLive) {
			// We thought this was a regular page, but the heartbeat shows it's now a live page
			// Need to disable editing since we're in an inconsistent state and show the convert-to-live flag
			setForceDisabled(true);
			showConvertToLivePageFlag({ converterWasCurrentUser: false });
		}

		const missedLivePagePublished = isLivePage && heartbeatData.heartbeatSubtype === null;
		if (missedLivePagePublished) {
			// We thought this was still a live page, but the heartbeat shows it's been published
			// Need to disable editing since we're working with outdated page state and show the published flag
			setForceDisabled(true);
			showPublishedFlagOnce(version, userId);
		}
	}, [
		heartbeatData.heartbeatSubtype,
		heartbeatData.subtypeMismatch,
		isLivePage,
		showConvertToLivePageFlag,
		showPublishedFlagOnce,
		userId,
		version,
	]);

	const spaceId = useSpaceId();

	useCollabPermissionChange({
		collabEditProvider,
		onPermissionChange,
	});

	const { collabInitTimedOut } = useCollabInitTimeout({
		contentId,
		contentType,
		collabEditInitialized,
		collabRecoveryError,
		collabEditError,
		isEditPageDraftRestricted: ssrPermissionsErrorHandler.getRestrictedRoute() === 'edit',
	});
	useCollaboratorLimits({
		contentId,
		spaceId,
		flags,
		collabEditProvider,
		isEditLimitExceeded: heartbeatData.collabLimitReached || heartbeatData.liveEditLimitReached,
		isLiveViewLimitExceeded: heartbeatData.liveViewLimitReached,
	});

	// analytics and experience tracking on NCS disconnection
	useNativeCollabConnectionListener({
		collabEditConnectionTimedOut,
		collabInitTimedOut,
	});

	// Editor expects collabEditProvider: Promise<CollabEditProvider> | undefined
	const collabEditProviderPromise = useMemo(() => {
		return collabEditProvider ? Promise.resolve(collabEditProvider) : undefined;
	}, [collabEditProvider]);

	// EDITOR-6492: stable per-mount clientID for prosemirror-collab, forwarded
	// via CollabEditOptions.userId. SSR-safe: never rendered to DOM.
	const [collabClientId] = useState(() => {
		if (!expValEquals('cc-markdown-mode', 'isEnabled', true)) {
			return undefined;
		}
		const id =
			typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
				? crypto.randomUUID()
				: uuid();
		return `pc-${id}`;
	});

	const collabEditOption = useMemo(() => {
		return {
			useNativePlugin: true,
			...(collabClientId && { userId: collabClientId }),
		};
	}, [collabClientId]);

	const collabEditStateOption = useMemo(() => {
		return {
			collabEditInitialized,
			collabEditError,
			collabEditInternetLost,
			collabRecoveryError,
			collabEditConnectionTimedOut,
		};
	}, [
		collabEditInitialized,
		collabEditError,
		collabEditInternetLost,
		collabRecoveryError,
		collabEditConnectionTimedOut,
	]);

	const internetDisconnected = useMemo(() => disconnected, [disconnected]);

	const onPublishUpdateTitleInPageTree = useCallback(() => {
		// Company hub doesn't show the left nav / page tree so this is not necessary and, more importantly, it causes https://hello.atlassian.net/browse/CHUB-601
		if (!isCompanyHub) {
			if (contentType === 'blogpost') {
				// force refetch blog tree when blogpost draft title changed on close or published with a new name
				forceRefetchBlogTree();
			} else {
				// otherwise, force refetch only the content tree
				pageTreeStateUpdater?.setForceFetchPageId(contentId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, isCompanyHub, contentType]);

	const optimisticDeleteItemInTree = useCallback(
		(id: string) => {
			pageTreeStateUpdater?.setOptimisticDeleteId(id);
		},
		[pageTreeStateUpdater],
	);

	let currentEditorPageLoadExperienceName = EDIT_PAGE_LOAD_EXPERIENCE;
	if (isLivePage) {
		currentEditorPageLoadExperienceName = VIEW_PAGE_EXPERIENCE;
	}
	if (isCompanyHub) {
		currentEditorPageLoadExperienceName = COMPANY_HUB_EDITOR_LOAD_EXPERIENCE;
	}

	return (
		<DraftSyncAndPublishContextConsumer>
			{({ isPublishing }) => (
				<>
					{childrenMemoized(children, {
						contentId,
						draftShareId,
						spaceKey,
						spaceId: memoizedDraftMetadata.spaceId,
						// Editor related props
						disabled:
							!collabEditInitialized ||
							loading ||
							!!collabEditError ||
							collabEditInternetLost ||
							disconnected ||
							forceDisabled ||
							hasExceededMaxStepFailures ||
							isPublishing ||
							collabEditConnectionTimedOut ||
							isLocked,
						setForceDisabled,
						internetDisconnected,
						forceDisabled,
						// Collab related props
						collabEditProvider: collabEditProviderPromise,
						collabEdit: collabEditOption,
						collabEditState: collabEditStateOption,
						heartbeatData,
						isFirstParticipant,
						isSingleUserSession,
						refetchNativeCollab: fetchNativeCollab,
						viewMode: isLivePage && initialViewMode,
						// Title related props
						// Note: title can be updated to an empty string
						title: title ?? titleFromInit,
						onTitleChange,
						onPublishUpdateTitleInPageTree,
						optimisticDeleteItemInTree,
						// Content query props
						contentType,
						isUnpublishedDraft,
						version,
						subType,
						isOverviewPage,
						createdDate: data?.content?.nodes?.[0]?.history?.createdDate,
					})}
					{error && (
						<ExperienceStop
							name={currentEditorPageLoadExperienceName}
							error={collabRecoveryError || error}
							attributes={{ collabService: 'ncs', isLivePage }}
						/>
					)}
				</>
			)}
		</DraftSyncAndPublishContextConsumer>
	);
};

export const NativeCollabComponent: React.ComponentType<
	Omit<Omit<any, 'pageTreeStateUpdater'>, 'flags'>
> = withFlags(
	withPageTreeStateUpdater(
		withEditorFlagsContext((props: NativeCollabProps) => (
			<DraftSyncAndPublishContextProvider>
				<NativeCollabComponentClass {...props} />
			</DraftSyncAndPublishContextProvider>
		)),
	),
);
